import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Divider,
} from "@mui/material";
import bgImage from "../../assets/legalstream-meeting.avif";
import lsLogo from "../../assets/LegalStreamLogo.avif";
import { useTheme, useMediaQuery } from "@mui/material";
import {
  signIn,
  fetchAuthSession,
  getCurrentUser,
  signOut,
} from "@aws-amplify/auth";
import { ALLOWED_USER_GROUP } from "../../awsConfig";
import LockIcon from "@mui/icons-material/Lock";

const Login = ({ onLoginSuccess }) => {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    restoreSession();
  }, []);

  const restoreSession = async () => {
    try {
      await getCurrentUser();
      await validateGroupAccess();
    } catch {
      console.log("No active session");
    }
  };

  const handleLogin = async () => {
    setLoading(true);
    setError("");

    try {
      const result = await signIn({ username, password });

      await validateGroupAccess();
    } catch (err) {
      setError(err.message || "Login failed");
      setLoading(false);
    }
  };

  const validateGroupAccess = async () => {
    const session = await fetchAuthSession();
    const token = session.tokens.idToken.payload;

    if (!token["cognito:groups"]?.includes(ALLOWED_USER_GROUP)) {
      await signOut({ global: true });
      throw new Error("Access Denied: Not Authorized User");
    }

    onLoginSuccess();
  };

  const handleDuoLogin = async () => {
    setLoading(true);
    setError("");

    try {
      // Implement your Duo authentication logic here
      // This would typically call your backend to initiate Duo auth flow
      console.log("Initiating Duo authentication");
      // await duoAuthService.authenticate();
    } catch (err) {
      setError(err.message || "Duo authentication failed");
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: `linear-gradient(135deg, #667eea 0%, #0c3c60 100%)`,
        backgroundImage: `url(${bgImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundAttachment: "fixed",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          bgcolor: "rgba(12, 60, 96, 0.55)",
          zIndex: 0,
        },
      }}
    >
      {/* Centered Logo */}
      <Box
        sx={{
          position: "fixed",
          top: isSm ? 16 : 30,
          left: isSm ? "50%" : "10%",
          transform: "translateX(-50%)",
          zIndex: 1000,
          pointerEvents: "none",
        }}
      >
        <img
          src={lsLogo}
          alt="Logo"
          style={{
            height: isSm ? "100px" : "140px",
            opacity: 0.9,
          }}
        />
      </Box>

      <Paper
        elevation={10}
        sx={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          maxWidth: "400px",
          mx: isSm ? 2 : 0,
          p: isSm ? 3 : 4,
          pt: isSm ? 18 : 8,
          borderRadius: 3,
          textAlign: "center",
          backgroundColor: "rgba(255,255,255,0.85)",
          backdropFilter: "blur(8px)",
        }}
      >
        <Typography
          variant={isSm ? "h6" : "h5"}
          sx={{ mb: 1, fontWeight: 600 }}
        >
          Hi, Welcome Back
        </Typography>
        <Typography variant="body2" sx={{ mb: 3, color: "text.secondary" }}>
          Please login to continue
        </Typography>

        <>
          <TextField
            label="Email"
            fullWidth
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              setError("");
            }}
            sx={{ mb: 2 }}
          />
          <TextField
            type="password"
            label="Password"
            fullWidth
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError("");
            }}
            sx={{ mb: 3 }}
          />
          <Button
            variant="contained"
            fullWidth
            onClick={handleLogin}
            disabled={loading}
            sx={{ py: 1.5 }}
          >
            {loading ? (
              <>
                <CircularProgress size={18} sx={{ mr: 1 }} />
                Signing in...
              </>
            ) : (
              "Login"
            )}
          </Button>
        </>

        {error && (
          <Typography color="error" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default Login;
